import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import FullScreenDialog from "@/components/FullScreenDialog";
import {
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, Sparkles, Layers, PlusCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";

/**
 * Re-sample modal — three ISA-grounded options:
 *   supplement       — same methodology, add more selections (excludes
 *                      already-sampled items)
 *   review_affected  — new sample containing ONLY items whose underlying GL
 *                      record was modified/removed by a head-wise Replace
 *                      upload (composite key: voucher_no + account_code)
 *   new              — draw a fresh sample, requires a justification note.
 *                      Old sample is preserved and linked via
 *                      parent_sample_id.
 */
export default function ResampleDialog({ open, onOpenChange, sample, onDone,
                                          onOpenHeadwise }) {
  const colW = useResizableColumns({ storageKey: "sampling-resample-dialog", defaults: [110, 110, 110, 240], mins: [72, 72, 72, 130] });   // Step 11d · add-on
  const { lang, dict, t } = useI18n();
  const [action, setAction] = useState("supplement");
  const [name, setName] = useState("");
  const [justification, setJustification] = useState("");
  const [sampleSize, setSampleSize] = useState("");
  const [running, setRunning] = useState(false);
  const [affectedPreview, setAffectedPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open) {
      setAction("supplement"); setName(""); setJustification("");
      setSampleSize(""); setAffectedPreview(null);
    }
  }, [open]);

  const hasSample = !!sample, sampleId = sample?.id;   // Step 7c · E3: the two parts of sample the preview reads
  useEffect(() => {
    if (!open || !hasSample || action !== "review_affected") {
      setAffectedPreview(null); return;
    }
    setLoadingPreview(true);
    api.get(`/samples/${sampleId}/affected-items`, { _silent: true })
      .then((r) => setAffectedPreview(r.data))
      .catch(() => setAffectedPreview({ affected: [], snapshot: null,
                                          message: t("resample.previewFailed") }))
      .finally(() => setLoadingPreview(false));
  }, [open, hasSample, sampleId, action, t]);

  const submit = async () => {
    if (!sample) return;
    setRunning(true);
    try {
      const body = {
        action,
        name: name.trim() || null,
        justification: justification || "",
      };
      if (action !== "review_affected" && sampleSize) {
        body.sample_size = Number(sampleSize);
      }
      const r = await api.post(`/samples/${sample.id}/resample`, body);
      toast.success(
        action === "review_affected"
          ? t("resample.createdAffected").replace("{n}", r.data.sample_size)
          : action === "supplement"
            ? t("resample.supplemented").replace("{n}", r.data.sample_size)
            : t("resample.newSampleDrawn").replace("{n}", r.data.sample_size),
      );
      onOpenChange(false);
      onDone?.(r.data);
    } catch (e) {
      toast.error(toErrMsg(e, t("resample.resampleFailed")));
    } finally { setRunning(false); }
  };

  const canSubmit = () => {
    if (!sample || running) return false;
    if (action === "review_affected") {
      return (affectedPreview?.affected?.length || 0) > 0;
    }
    if (action === "new") {
      return justification.trim().length > 0;
    }
    return true; // supplement — no extra requirement
  };

  return (
    <FullScreenDialog open={open} onOpenChange={onOpenChange}
                      testid="resample-dialog"
                      title={<>{t("resample.title")} — {sample?.name}</>}>
        <DialogDescription className="mb-3">
            {t("resample.headerDesc")}
        </DialogDescription>

        <RadioGroup value={action} onValueChange={setAction}
                    className="flex flex-col gap-2">
          <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                 data-testid="resample-supplement">
            <RadioGroupItem value="supplement" className="mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-1 text-sm font-medium">
                <PlusCircle className="h-4 w-4 text-emerald-600" /> {t("resample.supplementLabel")}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t("resample.supplementDesc")}
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                 data-testid="resample-review-affected">
            <RadioGroupItem value="review_affected" className="mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-1 text-sm font-medium">
                <Layers className="h-4 w-4 text-amber-600" /> {t("resample.reviewLabel")}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t("resample.reviewDesc")}
              </div>
            </div>
          </label>

          <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                 data-testid="resample-new">
            <RadioGroupItem value="new" className="mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-1 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-emerald-600" /> {t("resample.newLabel")}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t("resample.newDesc")}
              </div>
            </div>
          </label>
        </RadioGroup>

        {/* Contextual body per action */}
        {action === "supplement" && (
          <div className="rounded-md border bg-emerald-50/40 p-3 text-xs dark:bg-emerald-950/20">
            <div className="mb-1 font-medium">{t("resample.supplementOptions")}</div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-[10px] uppercase text-muted-foreground">
                  {t("resample.addlSize")}
                </Label>
                <Input type="number" min="1" value={sampleSize}
                       onChange={(e) => setSampleSize(e.target.value)}
                       placeholder={t("resample.defaultN").replace("{n}", sample?.sample_size || 5)}
                       className="h-8 text-xs"
                       data-testid="resample-size" />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] uppercase text-muted-foreground">
                  {t("resample.sheetName")}
                </Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t("resample.sheetSuffixSupplement").replace("{name}", sample?.name || "Sample")}
                       className="h-8 text-xs"
                       data-testid="resample-name" />
              </div>
            </div>
          </div>
        )}

        {action === "review_affected" && (
          <div className="rounded-md border bg-amber-50/40 p-3 text-xs dark:bg-amber-950/20"
               data-testid="resample-affected-panel">
            {loadingPreview && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> {t("resample.scanning")}
              </div>
            )}
            {!loadingPreview && affectedPreview && (
              <>
                {!affectedPreview.snapshot && (
                  <div className="text-amber-800 dark:text-amber-200">
                    {t("resample.noReplaceYet").split("{btn}")[0]}
                    <b>{t("resample.uploadHeadwise")}</b>
                    {t("resample.noReplaceYet").split("{btn}")[1]}
                  </div>
                )}
                {affectedPreview.snapshot && (
                  <>
                    <div className="mb-1">
                      {t("resample.comparingAgainst")}{" "}
                      <span className="font-mono text-[10px]">{affectedPreview.snapshot.head_upload_id.slice(0, 8)}</span>
                      {t("resample.fsHeadsLabel")}
                      {asArray(affectedPreview.snapshot.fs_codes).map((c) => (
                        <span key={c} className="me-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-[10px]">{c}</span>
                      ))}
                    </div>
                    <div className="font-medium">
                      {fmtPlural(affectedPreview.affected.length, lang, dict.plurals.items)} {t("resample.affected")}
                    </div>
                    {affectedPreview.affected.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto rounded border bg-white p-1 dark:bg-slate-950"
                           data-testid="resample-affected-list">
                        <ResizableTable cols={colW} className="w-full text-[10px]">
                          <thead className="bg-slate-50 dark:bg-slate-900">
                            <tr>
                              <ResizableTh index={0} className="px-1 py-0.5 text-start">{t("resample.uidHdr")}</ResizableTh>
                              <ResizableTh index={1} className="px-1 py-0.5 text-start">{t("resample.voucherHdr")}</ResizableTh>
                              <ResizableTh index={2} className="px-1 py-0.5 text-start">{t("resample.accountHdr")}</ResizableTh>
                              <ResizableTh index={3} className="px-1 py-0.5 text-start">{t("resample.reasonHdr")}</ResizableTh>
                            </tr>
                          </thead>
                          <tbody>
                            {affectedPreview.affected.slice(0, 20).map((a) => (
                              <tr key={a.vouching_item_id} className="border-t">
                                <td className="px-1 py-0.5 font-mono">{a.sample_item_uid}</td>
                                <td className="px-1 py-0.5 font-mono">{a.voucher_no}</td>
                                <td className="px-1 py-0.5">{a.account_code}</td>
                                <td className="px-1 py-0.5">
                                  <span className={`rounded px-1 py-0.5 text-[9px] font-semibold uppercase ${
                                    a.reason === "removed" ? "bg-red-100 text-red-800" :
                                    a.reason === "changed" ? "bg-amber-100 text-amber-800" :
                                    "bg-slate-100 text-slate-700"
                                  }`}>{a.reason}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </ResizableTable>
                        {affectedPreview.affected.length > 20 && (
                          <div className="p-1 text-center text-muted-foreground">
                            {t("resample.andMore").replace("{n}", affectedPreview.affected.length - 20)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {action === "new" && (
          <div className="rounded-md border bg-emerald-50/40 p-3 text-xs dark:bg-emerald-950/20">
            <div className="mb-1 font-medium">{t("resample.newDrawTitle")}</div>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">
                  {t("resample.justification")}
                </Label>
                <Textarea value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          rows={3}
                          placeholder={t("resample.justificationPh")}
                          className="text-xs"
                          data-testid="resample-justification" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">
                    {t("resample.sampleSize")}
                  </Label>
                  <Input type="number" min="1" value={sampleSize}
                         onChange={(e) => setSampleSize(e.target.value)}
                         placeholder={t("resample.defaultN").replace("{n}", sample?.sample_size || 5)}
                         className="h-8 text-xs"
                         data-testid="resample-size-new" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">
                    {t("resample.sheetName")}
                  </Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                         placeholder={t("resample.sheetSuffixNew").replace("{name}", sample?.name || "Sample")}
                         className="h-8 text-xs"
                         data-testid="resample-name-new" />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div>
            {onOpenHeadwise && (
              <Button variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-200"
                      onClick={() => { onOpenChange(false); onOpenHeadwise(); }}
                      data-testid="resample-open-headwise">
                <Upload className="me-1 h-3 w-3" /> {t("resample.uploadHeadwise")}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
                    data-testid="resample-cancel">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={!canSubmit()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="resample-submit">
              {running ? <><Loader2 className="me-1 h-3 w-3 animate-spin" /> {t("resample.running")}</> :
               <><RefreshCw className="me-1 h-3 w-3" /> {t("resample.createLinked")}</>}
            </Button>
          </div>
        </div>
    </FullScreenDialog>
  );
}
